import React, { useState, useEffect } from 'react';
import { db } from './firebase';
import { collection, query, where, getDocs, onSnapshot } from 'firebase/firestore';
import PlayerProfile from './PlayerProfile';

function FunStats({ onClose, currentUserId }) {
  const [view, setView] = useState('performance'); // performance, social, smoking, drinking
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState({});
  const [stats, setStats] = useState({
    gir: [],
    fairways: [],
    updown: [],
    cigarettes: [],
    borrowed: [],
    drinks: [],
    partners: [],
    sgp: []
  });
  const [selectedPlayer, setSelectedPlayer] = useState(null);

  // Load all users
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const usersMap = {};
      snapshot.docs.forEach(doc => {
        usersMap[doc.id] = doc.data();
      });
      setUsers(usersMap);
    });

    return unsubscribe;
  }, []);

  // Load and calculate stats
  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);

        // Get all approved rounds
        const roundsQuery = query(
          collection(db, 'rounds'),
          where('status', '==', 'approved')
        );

        const roundsSnapshot = await getDocs(roundsQuery);

        // Initialize stat tracking
        const userStats = {};
        const partnerStats = {};

        roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
          const round = doc.data();
          const userId = round.userId;

          if (!userStats[userId]) {
            userStats[userId] = {
              userId: userId,
              totalRounds: 0,
              totalGIR: 0,
              totalGIROpportunities: 0,
              totalFairways: 0,
              totalFairwayOpportunities: 0,
              totalUpDowns: 0,
              totalUpDownOpportunities: 0,
              totalCigarettes: 0,
              totalBorrowed: 0,
              totalDrinks: 0,
              totalPoints: 0,
              partnerRounds: {}
            };
          }

          const stat = userStats[userId];
          stat.totalRounds++;
          stat.totalGIR += round.greensInRegulation || 0;
          stat.totalGIROpportunities += 9; // Always 9 holes
          stat.totalFairways += round.fairwaysHit || 0;
          stat.totalFairwayOpportunities += round.fairwaysTotal || 0;
          stat.totalUpDowns += round.upAndDowns || 0;
          stat.totalUpDownOpportunities += round.upAndDownOpportunities || 0;
          stat.totalCigarettes += round.cigarettesSmoked || 0;
          stat.totalBorrowed += round.cigarettesBorrowed || 0;
          stat.totalDrinks += round.beersAfterRound || 0;
          stat.totalPoints += round.totalStableford || 0;

          // Track partner performance
          if (round.flightMembers && round.flightMembers.length > 0) {
            round.flightMembers.forEach(partnerId => {
              if (!stat.partnerRounds[partnerId]) {
                stat.partnerRounds[partnerId] = {
                  partnerId: partnerId,
                  rounds: 0,
                  totalPoints: 0
                };
              }
              stat.partnerRounds[partnerId].rounds++;
              stat.partnerRounds[partnerId].totalPoints += round.totalStableford || 0;
            });
          }
        });

        // Calculate percentages and create leaderboards
        const girLeaderboard = Object.values(userStats)
          .filter(s => s.totalGIROpportunities > 0)
          .map(s => ({
            userId: s.userId,
            gir: s.totalGIR,
            opportunities: s.totalGIROpportunities,
            percentage: Math.round((s.totalGIR / s.totalGIROpportunities) * 100),
            rounds: s.totalRounds
          }))
          .sort((a, b) => b.percentage - a.percentage);

        const fairwayLeaderboard = Object.values(userStats)
          .filter(s => s.totalFairwayOpportunities > 0)
          .map(s => ({
            userId: s.userId,
            fairways: s.totalFairways,
            opportunities: s.totalFairwayOpportunities,
            percentage: Math.round((s.totalFairways / s.totalFairwayOpportunities) * 100),
            rounds: s.totalRounds
          }))
          .sort((a, b) => b.percentage - a.percentage);

        const updownLeaderboard = Object.values(userStats)
          .filter(s => s.totalUpDownOpportunities > 0)
          .map(s => ({
            userId: s.userId,
            updowns: s.totalUpDowns,
            opportunities: s.totalUpDownOpportunities,
            percentage: Math.round((s.totalUpDowns / s.totalUpDownOpportunities) * 100),
            rounds: s.totalRounds
          }))
          .sort((a, b) => b.percentage - a.percentage);

        const cigaretteLeaderboard = Object.values(userStats)
          .map(s => ({
            userId: s.userId,
            total: s.totalCigarettes,
            perRound: s.totalRounds > 0 ? (s.totalCigarettes / s.totalRounds).toFixed(1) : 0,
            rounds: s.totalRounds
          }))
          .filter(s => s.total > 0)
          .sort((a, b) => b.total - a.total);

        const borrowedLeaderboard = Object.values(userStats)
          .map(s => ({
            userId: s.userId,
            total: s.totalBorrowed,
            perRound: s.totalRounds > 0 ? (s.totalBorrowed / s.totalRounds).toFixed(1) : 0,
            rounds: s.totalRounds
          }))
          .filter(s => s.total > 0)
          .sort((a, b) => b.total - a.total);

        const drinksLeaderboard = Object.values(userStats)
          .map(s => ({
            userId: s.userId,
            total: s.totalDrinks,
            perRound: s.totalRounds > 0 ? (s.totalDrinks / s.totalRounds).toFixed(1) : 0,
            rounds: s.totalRounds
          }))
          .filter(s => s.total > 0)
          .sort((a, b) => b.total - a.total);

        // Build field avg putts per event for SGP
        const puttsByEvent = {};
        roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
          const round = doc.data();
          if (!round.eventId || !round.totalPutts) return;
          if (!puttsByEvent[round.eventId])
            puttsByEvent[round.eventId] = { total: 0, count: 0 };
          puttsByEvent[round.eventId].total += round.totalPutts;
          puttsByEvent[round.eventId].count++;
        });

        // Compute per-player season SGP
        const sgpByPlayer = {};
        roundsSnapshot.docs.filter(d => !d.data().isFinale).forEach(doc => {
          const round = doc.data();
          const uid = round.userId;
          if (!round.eventId || !round.totalPutts) return;
          const fieldAvg = puttsByEvent[round.eventId]
            ? puttsByEvent[round.eventId].total / puttsByEvent[round.eventId].count
            : null;
          if (fieldAvg === null) return;
          if (!sgpByPlayer[uid]) sgpByPlayer[uid] = { total: 0, count: 0 };
          sgpByPlayer[uid].total += fieldAvg - round.totalPutts;
          sgpByPlayer[uid].count++;
        });

        const sgpLeaderboard = Object.entries(sgpByPlayer)
          .map(([uid, val]) => ({
            userId: uid,
            avgSGP: parseFloat((val.total / val.count).toFixed(2)),
            rounds: val.count
          }))
          .filter(p => p.rounds >= 1)
          .sort((a, b) => b.avgSGP - a.avgSGP);

        // Partner analysis for current user
        const currentUserStats = userStats[currentUserId];
        let partnerLeaderboard = [];

        if (currentUserStats && currentUserStats.partnerRounds) {
          partnerLeaderboard = Object.values(currentUserStats.partnerRounds)
            .map(p => ({
              partnerId: p.partnerId,
              rounds: p.rounds,
              totalPoints: p.totalPoints,
              avgPoints: Math.round(p.totalPoints / p.rounds)
            }))
            .filter(p => p.rounds >= 2) // Must have played at least 2 rounds together
            .sort((a, b) => b.avgPoints - a.avgPoints);
        }

        setStats({
          gir: girLeaderboard,
          fairways: fairwayLeaderboard,
          updown: updownLeaderboard,
          cigarettes: cigaretteLeaderboard,
          borrowed: borrowedLeaderboard,
          drinks: drinksLeaderboard,
          partners: partnerLeaderboard,
          sgp: sgpLeaderboard
        });

      } catch (error) {
        console.error('Error loading stats:', error);
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, [currentUserId]);

  const getUserName = (userId) => {
    return users[userId]?.name || 'Unknown';
  };

  const getUserPhoto = (userId) => {
    return users[userId]?.photoURL || null;
  };

  const getUserInitial = (userId) => {
    const name = users[userId]?.name || '?';
    return name.charAt(0).toUpperCase();
  };

  const getMedalEmoji = (position) => {
    if (position === 0) return '🥇';
    if (position === 1) return '🥈';
    if (position === 2) return '🥉';
    return '';
  };

  const LeaderCard = ({ player, index, title, subtitle }) => {
    const photoURL = getUserPhoto(player.userId);
    const initial = getUserInitial(player.userId);

    return (
      <div style={styles.leaderCard}>
        <div style={styles.leaderPhotoContainer}>
          {photoURL ? (
            <img src={photoURL} alt={getUserName(player.userId)} style={styles.leaderPhoto} />
          ) : (
            <div style={styles.leaderPhotoPlaceholder}>{initial}</div>
          )}
          <div style={styles.leaderBadge}>{getMedalEmoji(index)}</div>
        </div>
        <div style={styles.leaderInfo}>
          <div style={styles.leaderTitle}>{title}</div>
          <div style={styles.leaderName}>{getUserName(player.userId)}</div>
          <div style={styles.leaderSubtitle}>{subtitle}</div>
        </div>
      </div>
    );
  };

  if (selectedPlayer) {
    return (
      <PlayerProfile
        userId={selectedPlayer}
        onClose={() => setSelectedPlayer(null)}
      />
    );
  }

  return (
    <div style={styles.overlay}>
      <div style={styles.card}>
        <div style={styles.header}>
          <h2 style={styles.title}>📊 Statistic Leaders</h2>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <div style={styles.tabs}>
          <button
            onClick={() => setView('performance')}
            style={view === 'performance' ? styles.tabActive : styles.tab}
          >
            🎯 Performance
          </button>
          <button
            onClick={() => setView('smoking')}
            style={view === 'smoking' ? styles.tabActive : styles.tab}
          >
            🚬 Smoking
          </button>
          <button
            onClick={() => setView('drinking')}
            style={view === 'drinking' ? styles.tabActive : styles.tab}
          >
            🍺 Drinking
          </button>
          <button
            onClick={() => setView('social')}
            style={view === 'social' ? styles.tabActive : styles.tab}
          >
            👥 Partners
          </button>
        </div>

        <div style={styles.content}>
          {loading ? (
            <div style={styles.loadingState}>Loading stats...</div>
          ) : (
            <>
              {view === 'performance' && (
                <div style={styles.sectionContainer}>
                  {/* Leader Cards */}
                  {stats.gir.length > 0 && (
                    <LeaderCard
                      player={stats.gir[0]}
                      index={0}
                      title="🎯 GIR Leader"
                      subtitle={`${stats.gir[0].percentage}% greens hit`}
                    />
                  )}
                  {stats.fairways.length > 0 && (
                    <LeaderCard
                      player={stats.fairways[0]}
                      index={0}
                      title="🏌️ Fairway King"
                      subtitle={`${stats.fairways[0].percentage}% accuracy`}
                    />
                  )}
                  {stats.updown.length > 0 && (
                    <LeaderCard
                      player={stats.updown[0]}
                      index={0}
                      title="⛳ Short Game Wizard"
                      subtitle={`${stats.updown[0].percentage}% up & down`}
                    />
                  )}
                  {stats.sgp.length > 0 && (
                    <LeaderCard
                      player={stats.sgp[0]}
                      index={0}
                      title="🎯 Putting Master"
                      subtitle={`${stats.sgp[0].avgSGP > 0 ? '+' : ''}${stats.sgp[0].avgSGP} strokes gained`}
                    />
                  )}

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🎯 Greens in Regulation Leaders</h3>
                    {stats.gir.length === 0 ? (
                      <p style={styles.emptyState}>No data yet</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.gir.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.percentage}%</strong>
                            </div>
                            <div style={styles.statDetail}>
                              {player.gir}/{player.opportunities}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🏌️ Fairway Accuracy Leaders</h3>
                    {stats.fairways.length === 0 ? (
                      <p style={styles.emptyState}>No data yet</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.fairways.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.percentage}%</strong>
                            </div>
                            <div style={styles.statDetail}>
                              {player.fairways}/{player.opportunities}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>⛳ Up & Down Kings</h3>
                    {stats.updown.length === 0 ? (
                      <p style={styles.emptyState}>No data yet</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.updown.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.percentage}%</strong>
                            </div>
                            <div style={styles.statDetail}>
                              {player.updowns}/{player.opportunities}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🎯 Strokes Gained Putting</h3>
                    <p style={styles.sectionSubtitle}>
                      Positive = better than field average
                    </p>
                    {stats.sgp.length === 0 ? (
                      <p style={styles.emptyState}>No putting data yet</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.sgp.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={{
                              ...styles.statValue,
                              color: player.avgSGP > 0 ? '#10b981'
                                   : player.avgSGP < 0 ? '#ef4444'
                                   : '#9ca3af'
                            }}>
                              <strong>
                                {player.avgSGP > 0 ? '+' : ''}{player.avgSGP}
                              </strong>
                            </div>
                            <div style={styles.statDetail}>
                              {player.rounds} rounds
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {view === 'smoking' && (
                <div style={styles.sectionContainer}>
                  {/* Leader Cards */}
                  {stats.cigarettes.length > 0 && (
                    <LeaderCard
                      player={stats.cigarettes[0]}
                      index={0}
                      title="🚬 Chimney Champion"
                      subtitle={`${stats.cigarettes[0].total} cigarettes smoked`}
                    />
                  )}
                  {stats.borrowed.length > 0 && (
                    <LeaderCard
                      player={stats.borrowed[0]}
                      index={0}
                      title="🚬 Biggest Mooch"
                      subtitle={`${stats.borrowed[0].total} cigarettes borrowed`}
                    />
                  )}

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🚬 Most Cigarettes Smoked</h3>
                    {stats.cigarettes.length === 0 ? (
                      <p style={styles.emptyState}>No one smoking yet! 😇</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.cigarettes.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.total}</strong> total
                            </div>
                            <div style={styles.statDetail}>
                              {player.perRound}/round
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🚬 Biggest Mooch</h3>
                    {stats.borrowed.length === 0 ? (
                      <p style={styles.emptyState}>Everyone brought their own! 👏</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.borrowed.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.total}</strong> borrowed
                            </div>
                            <div style={styles.statDetail}>
                              {player.perRound}/round
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {view === 'drinking' && (
                <div style={styles.sectionContainer}>
                  {/* Leader Card */}
                  {stats.drinks.length > 0 && (
                    <LeaderCard
                      player={stats.drinks[0]}
                      index={0}
                      title="🍺 Party Animal"
                      subtitle={`${stats.drinks[0].total} drinks consumed`}
                    />
                  )}

                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>🍺 Most Drinks Consumed</h3>
                    {stats.drinks.length === 0 ? (
                      <p style={styles.emptyState}>No drinks recorded! 🚱</p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.drinks.slice(0, 10).map((player, index) => (
                          <div
                            key={player.userId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(player.userId)}
                          >
                            <div style={styles.position}>
                              {getMedalEmoji(index)} {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(player.userId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{player.total}</strong> total
                            </div>
                            <div style={styles.statDetail}>
                              {player.perRound}/round
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {view === 'social' && (
                <div style={styles.sectionContainer}>
                  <div style={styles.statSection}>
                    <h3 style={styles.sectionTitle}>👥 Your Best Playing Partners</h3>
                    <p style={styles.sectionSubtitle}>Who brings out your A-game?</p>
                    {stats.partners.length === 0 ? (
                      <p style={styles.emptyState}>
                        Play at least 2 rounds with someone to see partner stats!
                      </p>
                    ) : (
                      <div style={styles.leaderboard}>
                        {stats.partners.map((partner, index) => (
                          <div
                            key={partner.partnerId}
                            style={styles.statRow}
                            onClick={() => setSelectedPlayer(partner.partnerId)}
                          >
                            <div style={styles.position}>
                              {index + 1}
                            </div>
                            <div style={styles.playerName}>
                              {getUserName(partner.partnerId)}
                            </div>
                            <div style={styles.statValue}>
                              <strong>{partner.avgPoints}</strong> avg
                            </div>
                            <div style={styles.statDetail}>
                              {partner.rounds} rounds
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
    overflowY: 'auto',
    overflowX: 'hidden',
    zIndex: 1000,
    animation: 'fadeIn 0.2s ease'
  },
  card: {
    backgroundColor: '#1e1e1e',
    borderRadius: '12px',
    boxShadow: '0 8px 24px rgba(0,0,0,0.6)',
    border: '1px solid #2d2d2d',
    width: '100%',
    maxWidth: '1000px',
    maxHeight: '90vh',
    display: 'flex',
    flexDirection: 'column',
    animation: 'slideUp 0.25s ease'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 20px',
    borderBottom: '1px solid #2d2d2d',
    flexShrink: 0
  },
  title: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#ffffff',
    margin: 0,
    letterSpacing: '-0.01em'
  },
  closeButton: {
    backgroundColor: 'transparent',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#9ca3af',
    padding: '5px 10px',
    transition: 'all 0.3s ease'
  },
  tabs: {
    display: 'flex',
    borderBottom: '1px solid #2d2d2d',
    padding: '0 15px',
    gap: '2px',
    flexShrink: 0,
    overflowX: 'auto'
  },
  tab: {
    padding: '12px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid transparent',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '500',
    color: '#9ca3af',
    whiteSpace: 'nowrap',
    transition: 'all 0.3s ease'
  },
  tabActive: {
    padding: '12px 14px',
    backgroundColor: 'transparent',
    border: 'none',
    borderBottom: '2px solid #d4af37',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '600',
    color: '#d4af37',
    whiteSpace: 'nowrap'
  },
  content: {
    flex: 1,
    overflowY: 'auto',
    padding: '20px'
  },
  loadingState: {
    textAlign: 'center',
    padding: '60px 20px',
    color: '#6b7280',
    fontSize: '16px',
    fontWeight: '400'
  },
  sectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '30px'
  },
  leaderCard: {
    background: 'linear-gradient(135deg, #0f0f0f 0%, #1a1a1a 100%)',
    border: '2px solid #d4af37',
    borderRadius: '16px',
    padding: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '18px',
    boxShadow: '0 8px 20px rgba(0,0,0,0.4)',
    marginBottom: '10px'
  },
  leaderPhotoContainer: {
    position: 'relative',
    flexShrink: 0
  },
  leaderPhoto: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    objectFit: 'cover',
    border: '3px solid #d4af37',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
  },
  leaderPhotoPlaceholder: {
    width: '72px',
    height: '72px',
    borderRadius: '50%',
    backgroundColor: '#d4af37',
    border: '3px solid #2d2d2d',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '30px',
    fontWeight: '700',
    color: '#0f0f0f',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)'
  },
  leaderBadge: {
    position: 'absolute',
    bottom: '-5px',
    right: '-5px',
    fontSize: '22px',
    backgroundColor: '#1e1e1e',
    borderRadius: '50%',
    padding: '3px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.4)'
  },
  leaderInfo: {
    flex: 1,
    minWidth: 0
  },
  leaderTitle: {
    fontSize: '12px',
    color: '#d4af37',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.1em',
    marginBottom: '5px'
  },
  leaderName: {
    fontSize: '22px',
    fontWeight: '700',
    color: '#ffffff',
    marginBottom: '5px',
    letterSpacing: '-0.02em'
  },
  leaderSubtitle: {
    fontSize: '13px',
    color: '#d4af37',
    fontWeight: '600'
  },
  statSection: {
    marginBottom: '20px'
  },
  sectionTitle: {
    fontSize: '17px',
    fontWeight: '600',
    color: '#ffffff',
    margin: '0 0 5px 0',
    letterSpacing: '-0.01em'
  },
  sectionSubtitle: {
    fontSize: '14px',
    color: '#9ca3af',
    margin: '0 0 20px 0',
    fontWeight: '400'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#6b7280',
    fontSize: '15px',
    fontStyle: 'italic',
    fontWeight: '400'
  },
  leaderboard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '10px'
  },
  statRow: {
    display: 'grid',
    gridTemplateColumns: '40px minmax(0, 1fr) auto auto',
    gap: '8px',
    padding: '12px 14px',
    backgroundColor: '#0f0f0f',
    borderRadius: '8px',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    border: '1px solid #2d2d2d'
  },
  position: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#9ca3af',
    textAlign: 'center'
  },
  playerName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#ffffff',
    letterSpacing: '-0.01em'
  },
  statValue: {
    fontSize: '14px',
    color: '#d4af37',
    textAlign: 'right',
    fontWeight: '600',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  statDetail: {
    fontSize: '12px',
    color: '#9ca3af',
    textAlign: 'right',
    fontWeight: '500',
    whiteSpace: 'nowrap'
  }
};

export default FunStats;
